import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../services/api';

/**
 * Test Viewer Component
 * Handles test taking with timer, questions, and submission
 */
const TestViewer = ({ test, moduleId: _moduleId, onTestComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startedAt, setStartedAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const questions = test?.questions?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];

  useEffect(() => {
    // Start test when component mounts
    const now = new Date();
    setStartedAt(now);

    if (test?.time_limit_minutes) {
      setTimeRemaining(test.time_limit_minutes * 60); // Convert to seconds
    }
  }, [test]);

  useEffect(() => {
    // Timer countdown
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const handleAutoSubmit = async () => {
    if (submitting) return;
    await handleSubmit(true);
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleMultipleChoiceChange = (questionId, optionIndex) => {
    const currentAnswers = answers[questionId] || [];
    const newAnswers = currentAnswers.includes(optionIndex)
      ? currentAnswers.filter((idx) => idx !== optionIndex)
      : [...currentAnswers, optionIndex];

    setAnswers((prev) => ({
      ...prev,
      [questionId]: newAnswers,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const response = await api.post(`/api/training/tests/${test.id}/submit`, {
        answers,
        startedAt: startedAt.toISOString(),
      });

      if (onTestComplete) {
        onTestComplete(response.data);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Ошибка при отправке теста. Пожалуйста, попробуйте еще раз.');
      setSubmitting(false);
    }
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!test || questions.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">Тест не найден</p>
      </div>
    );
  }

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">{test.title}</h2>
          {timeRemaining !== null && (
            <div className={`text-lg font-semibold ${timeRemaining < 300 ? 'text-red-600' : 'text-blue-600'}`}>
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {formatTime(timeRemaining)}
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Вопрос {currentQuestionIndex + 1} из {questions.length}</span>
            <span>Отвечено: {getAnsweredCount()} из {questions.length}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="mb-6">
          <div className="flex items-start mb-4">
            <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full mr-3">
              {currentQuestion.points || 10} баллов
            </span>
            <span className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full">
              {currentQuestion.type === 'single_choice' ? 'Один вариант' : 'Несколько вариантов'}
            </span>
          </div>

          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            {currentQuestion.question}
          </h3>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              const isSelected = currentQuestion.type === 'single_choice'
                ? answers[currentQuestion.id] === index
                : (answers[currentQuestion.id] || []).includes(index);

              return (
                <label
                  key={index}
                  className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                >
                  <div className="flex items-center">
                    <input
                      type={currentQuestion.type === 'single_choice' ? 'radio' : 'checkbox'}
                      name={`question-${currentQuestion.id}`}
                      checked={isSelected}
                      onChange={() =>
                        currentQuestion.type === 'single_choice'
                          ? handleAnswerChange(currentQuestion.id, index)
                          : handleMultipleChoiceChange(currentQuestion.id, index)
                      }
                      className="w-5 h-5 text-blue-600 mr-3"
                    />
                    <span className="text-gray-700">{option}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="space-y-6">
        {/* Question Numbers Grid/Flex */}
        <div className="flex flex-wrap justify-center gap-2 p-4 bg-gray-50 rounded-lg border border-gray-100">
          {questions.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentQuestionIndex(index)}
              title={`Вопрос ${index + 1}`}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-semibold text-sm sm:text-base transition-all ${index === currentQuestionIndex
                  ? 'bg-blue-600 text-white shadow-md transform scale-110 z-10'
                  : answers[questions[index].id] !== undefined
                    ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
                    : 'bg-white text-gray-600 border border-gray-300 hover:border-blue-400 hover:text-blue-600'
                }`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <button
            onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
            className="flex items-center px-4 sm:px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Назад</span>
          </button>

          <div className="text-sm font-medium text-gray-500">
            Вопрос {currentQuestionIndex + 1} / {questions.length}
          </div>

          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all font-bold"
            >
              {submitting ? 'Отправка...' : 'Завершить тест'}
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1))}
              className="flex items-center px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 shadow-md transition-all font-bold"
            >
              <span className="hidden sm:inline mr-2">Далее</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

TestViewer.propTypes = {
  test: PropTypes.object.isRequired,
  moduleId: PropTypes.number.isRequired,
  onTestComplete: PropTypes.func.isRequired,
};

export default TestViewer;
